import { signal, computed, batch } from "@preact/signals";
import { hasMapLocation, haversineKm } from "../utils/geo";
import { countryDisplayName } from "../utils/format";
import type { CityCount } from "../data/api";
import type {
  City,
  Composer,
  Concert,
  HistoricalConcert,
  ListItem,
  Meta,
  Org,
  Piece,
  SourceInfo,
} from "../types";

// Fallback slider bounds for an export whose meta.json predates the `historical` field.
// The real archive's true span starts 1842; the max is "next season" — the live pipeline
// lists concerts up to roughly a year ahead, and a whole-year ceiling never clips data.
const FALLBACK_HISTORY_MIN_YEAR = 1842;
export const SLIDER_MAX_YEAR = new Date().getFullYear() + 1;

/** The one pseudo-source that isn't an archive: the live scraping pipeline. It sits in the
 *  same Sources facet as the archives — "upcoming vs historical" is not a mode of this app,
 *  just a property of where a record came from and when it happens. */
export const LIVE_SOURCE_KEY = "live";

// ── Static aggregates (loaded once from JSON) ──────────────────────

export const cities = signal<City[]>([]);
export const composers = signal<Composer[]>([]);
export const orgs = signal<Org[]>([]);
export const meta = signal<Meta | null>(null);
/** composers.json + orgs.json are deferred (they only serve search/explore/chips) —
 *  see data/loader.ts's ensureFacets(). */
export const facetsLoaded = signal(false);
export const citiesLoaded = signal(false);
export const dataError = signal<string | null>(null);
export const mapError = signal<string | null>(null);

// ── Sources facet ──────────────────────────────────────────────────

/** The historical sources present in D1 (from /api/historical-sources), each with counts
 *  and a year span. The live pipeline is prepended as a pseudo-entry by consumers. */
export const archiveSources = signal<SourceInfo[]>([]);
export const archiveSourcesLoaded = signal(false);

/** Exclusion set — sources default ON (including sources that appear in a future export,
 *  which an inclusion set would silently drop). Contents: `"live"`, explicit archive
 *  source keys, and two pre-load placeholders from legacy URLs (`"_archive"` = every
 *  archive source, `"_probable"` = every non-confirmed source) that data/loader.ts expands
 *  into explicit keys once the source list arrives. */
export const disabledSources = signal<Set<string>>(new Set());

export const liveEnabled = computed(() => !disabledSources.value.has(LIVE_SOURCE_KEY));

/** Is at least one archive source enabled? (Before the source list loads, assume yes —
 *  the placeholder `_archive` is the explicit "none" marker.) */
export const archiveEnabled = computed(() => {
  const disabled = disabledSources.value;
  if (disabled.has("_archive")) return false;
  const sources = archiveSources.value;
  if (sources.length === 0) return true;
  return sources.some(
    (s) => !disabled.has(s.key) && !(disabled.has("_probable") && s.status !== "confirmed"),
  );
});

export function isSourceEnabled(source: SourceInfo): boolean {
  const disabled = disabledSources.value;
  if (disabled.has("_archive") || disabled.has(source.key)) return false;
  if (disabled.has("_probable") && source.status !== "confirmed") return false;
  return true;
}

/** Explicit archive source keys to exclude server-side (the `xsrc` param). Placeholders are
 *  expanded against the loaded source list; before it loads the caller should be gating on
 *  archiveEnabled anyway. */
export const excludedArchiveKeys = computed<string[]>(() => {
  const disabled = disabledSources.value;
  const keys = new Set<string>();
  for (const k of disabled) {
    if (k !== LIVE_SOURCE_KEY && k !== "_archive" && k !== "_probable") keys.add(k);
  }
  if (disabled.has("_probable")) {
    for (const s of archiveSources.value) if (s.status !== "confirmed") keys.add(s.key);
  }
  return [...keys].sort();
});

export function toggleSource(key: string) {
  const next = new Set(disabledSources.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  disabledSources.value = next;
}

/** Enable exactly one source (Explore's "Only" action). */
export function soloSource(key: string) {
  const all = new Set<string>([LIVE_SOURCE_KEY, ...archiveSources.value.map((s) => s.key)]);
  all.delete(key);
  disabledSources.value = all;
}

export function enableAllSources() {
  disabledSources.value = new Set();
}

/** Disable every source at once (the facet's "Clear all") — the starting point for
 *  "show me just one archive" without unticking a growing list source by source.
 *  Falls back to the `_archive` placeholder if the source list hasn't arrived yet
 *  (data/loader.ts expands it into explicit keys when it does). */
export function disableAllSources() {
  const keys = archiveSources.value.map((s) => s.key);
  disabledSources.value = new Set([LIVE_SOURCE_KEY, ...(keys.length ? keys : ["_archive"])]);
}

// ── Time window (the ONE time axis) ────────────────────────────────
//
// One [timeFrom, timeTo] window covers everything from the archive's first year to next
// season. The slider writes year-aligned bounds; the precise date inputs write exact days;
// both edit the same two signals. An empty timeTo means "no end bound".
//
// The default is the FULL span — narrowing is something the user does, not something the
// app pre-decides. What keeps "all of history by default" from burying today's concerts is
// the list anchor (see `anchorISO`): the reading list starts at today and pages forward,
// with the past reachable as its own direction.

export const historyBoundsMinYear = signal(FALLBACK_HISTORY_MIN_YEAR);
export const timeFrom = signal(`${FALLBACK_HISTORY_MIN_YEAR}-01-01`);
export const timeTo = signal("");
/** Set when ?from/?to (or legacy ?yfrom/?yto) arrived in the URL, so loader.ts won't
 *  clobber a shared link's window when meta.json supplies the real default bounds. */
export const timeWindowFromURL = signal(false);
/** Set on any user edit — url-sync only serializes the window once it's deliberate. */
export const timeWindowTouched = signal(false);

export function setTimeWindow(from: string, to: string) {
  batch(() => {
    timeFrom.value = from;
    timeTo.value = to;
    timeWindowTouched.value = true;
  });
}

export function resetTimeWindow() {
  batch(() => {
    timeFrom.value = `${historyBoundsMinYear.value}-01-01`;
    timeTo.value = "";
    timeWindowTouched.value = false;
  });
}

/** Clear one edge of the window (the date inputs' "×") — the matching slider handle snaps
 *  back too, since both controls edit the same signals. Collapsing to the full span goes
 *  through resetTimeWindow so `timeWindowTouched` clears and url-sync drops the params. */
export function clearTimeFrom() {
  if (!timeTo.value) resetTimeWindow();
  else setTimeWindow(`${historyBoundsMinYear.value}-01-01`, timeTo.value);
}
export function clearTimeTo() {
  if (timeFrom.value <= `${historyBoundsMinYear.value}-01-01`) resetTimeWindow();
  else setTimeWindow(timeFrom.value, "");
}

export const timeFromYear = computed(() => yearOfISO(timeFrom.value) ?? historyBoundsMinYear.value);
export const timeToYear = computed(() => (timeTo.value ? yearOfISO(timeTo.value) ?? SLIDER_MAX_YEAR : SLIDER_MAX_YEAR));

/** Today, if it falls inside the window — the date the list anchors on, splitting it into
 *  an "Upcoming" direction (today → window end) and a "Past" one (window start → today).
 *  Null when the window excludes today: then there's just one chronological list. */
export const anchorISO = computed<string | null>(() => {
  const today = todayISO();
  if (today < timeFrom.value) return null;
  if (timeTo.value && today > timeTo.value) return null;
  return today;
});

// ── Certainty (confidence tier of historical events) ───────────────
//
// The historical layer grades events by how well the source pins them down: 'confirmed'
// (institutional record), 'probable' (a single credible source at ≤month precision), and
// 'possible' — memoir events the source never dated to a day ("Winter 1812/13", "1808") or
// placed exactly. All three tiers ship by default — memoir sources are mostly 'possible'-tier,
// so hiding that tier made Explore's per-source counts wildly overstate what a click-through
// showed. Possible events appear INLINE in the list and on the map, badged 'possible' — they
// already carry a fuzzy [earliest,latest] span the slider can place, so no shape is faked.
// The toggle lets a reader restrict to the high-certainty tiers.

/** Include the 'possible' tier of historical events. Default on. */
export const showPossible = signal(true);

/** Certainty allowlist for the historical API (`cert=`): confirmed+probable always, +possible
 *  when opted in. */
export const certParam = computed<string[]>(() =>
  showPossible.value ? ["confirmed", "probable", "possible"] : ["confirmed", "probable"],
);

export function toggleShowPossible() {
  showPossible.value = !showPossible.value;
}

// ── Non-classical and kids' events (the opt-in toggle) ─────────────
//
// Off by default: the site is the classical site. On, the list and the map also show what the
// export's editorial gates hide (api/export.py EXTRA_CLASSES), each card labelled with what it is.
// Those rows live in parallel D1 tables, so nothing is fetched from them while this is off.
// Remembered on this device; a link carries it as `extras=1` without changing the viewer's own
// setting. Browse only: Explore's counts stay classical concerts.

const SHOW_EXTRAS_KEY = "showExtras";

export const showExtras = signal(false);

export function storedShowExtras(): boolean {
  try {
    return localStorage.getItem(SHOW_EXTRAS_KEY) === "1";
  } catch {
    return false;
  }
}

export function toggleShowExtras() {
  showExtras.value = !showExtras.value;
  try {
    if (showExtras.value) localStorage.setItem(SHOW_EXTRAS_KEY, "1");
    else localStorage.removeItem(SHOW_EXTRAS_KEY);
  } catch {
    // Private window or blocked storage: the toggle still works for this visit.
  }
}

// ── Mentions Reader ────────────────────────────────────────────────
//
// The discoverability layer for grounded quotes that can't be a map dot or a slider point —
// undated, placeless memoir passages that still NAME a composer. Opened from the composer
// filter; see components/MentionsReader.tsx and data/api.ts fetchHistoricalMentions.

/** Open/closed state of the Mentions Reader drawer. */
export const mentionsReaderOpen = signal(false);
/** Count of grounded mentions for the selected composer (null before the probe returns / no
 *  composer selected). Drives the "Read N mentions of {composer}" affordance. */
export const mentionCount = signal<number | null>(null);
/** When set, the Reader is in SOURCE mode: it shows one whole memoir/letters source's mentions
 *  (opened from Explore → Sources) rather than one composer's. Null ⇒ composer mode. */
export const mentionsReaderSource = signal<{ key: string; title: string } | null>(null);

/** Open the Reader in composer mode (the list banner). */
export function openMentionsReader() {
  batch(() => {
    mentionsReaderSource.value = null;
    mentionsReaderOpen.value = true;
  });
}
/** Open the Reader in source mode — browse one whole source (Explore → Sources). */
export function openSourceMentions(key: string, title: string) {
  batch(() => {
    mentionsReaderSource.value = { key, title };
    mentionsReaderOpen.value = true;
  });
}
export function closeMentionsReader() {
  batch(() => {
    mentionsReaderOpen.value = false;
    mentionsReaderSource.value = null;
  });
}

// ── Filters ────────────────────────────────────────────────────────

export const selectedComposerId = signal<number | null>(null);
export const selectedOrgId = signal<number | null>(null);

// The piece filter is split across two signals because, unlike composers/orgs/cities, the
// client has no local facet list to look a key up in (there are ~33k pieces — they live in
// D1 and are searched server-side). `selectedPieceKey` is the source of truth and the only
// thing the URL and the queries need; `selectedPiece` is the label, which is either carried
// over from the autocomplete selection or fetched by key when a shared URL is opened cold.
export const selectedPieceKey = signal<string | null>(null);
export const selectedPiece = signal<Piece | null>(null);

/** The catalogue work id inside a piece facet key, or null for an unresolved ("d…")
 *  facet. Key shapes (api/pieces.py piece_key): `w<work_id>[~<part_kind>]` resolved,
 *  `d<composer_id>~<hash>[~<part_kind>]` unresolved. The part suffix is deliberately
 *  dropped: a mention of a work is a mention whether the program billed the overture or
 *  the whole opera. */
export function pieceWorkId(key: string | null): number | null {
  if (!key) return null;
  const m = /^w(\d+)(?:~|$)/.exec(key);
  return m ? Number(m[1]) : null;
}
export const selectedCityId = signal<number | null>(null);
export const selectedCitySource = signal<"manual" | "popup" | null>(null);
/** "Within N km" modifier on the manual city filter (the radius search). null = exact
 *  city match, the long-standing behavior. Only meaningful with a manually-picked city —
 *  popup-sourced city filters are transient and never widen into a radius. */
export const radiusKm = signal<number | null>(null);
export const selectedCountry = signal("");

/** Genre facet. Values mirror FACET_GENRES in api/export.py; "" = no genre filter.
 *  Classification is deliberately partial (derived from the works catalogue, program
 *  titles, event verdicts and presenter type) — the panel says so while one is active. */
export const GENRE_OPTIONS: { value: string; label: string }[] = [
  { value: "orchestral", label: "Orchestral" },
  { value: "opera", label: "Opera & musical theatre" },
  { value: "ballet", label: "Ballet & dance" },
  { value: "chamber", label: "Chamber music" },
  { value: "choral", label: "Choral & vocal" },
  { value: "keyboard", label: "Keyboard & recital" },
];
export const selectedGenre = signal("");

export const viewMode = signal<"browse" | "explore">("browse");

// ── Explore-only state ───────────────────────────────────────────────
//
// Explore aggregates over the SAME filters Browse lists with (composer, org, piece, genre,
// city, country, sources, time window) plus three that only the facet API understands —
// they narrow Explore's counts but have no Browse equivalent, so "Show on the map" drops
// them (the CTA says so). URL-synced like everything else (`scope=`, `xera=`, `alive=1`,
// `premiere=1`).

/** Which side of today Explore aggregates: the upcoming season(s), past seasons + the
 *  archive, or everything. Intersects with the shared time window when that's deliberate. */
export type ExploreScope = "upcoming" | "past" | "all";
export const exploreScope = signal<ExploreScope>("upcoming");
/** Composer birth half-century (e.g. 1750 = born 1750–1799), or null. */
export const exploreEra = signal<number | null>(null);
/** Only concerts with at least one composer alive at the concert date. */
export const exploreAlive = signal(false);
/** Archive only: premieres. */
export const explorePremiere = signal(false);

export const exploreOnlyFilterCount = computed(
  () => (exploreEra.value !== null ? 1 : 0) + (exploreAlive.value ? 1 : 0) + (explorePremiere.value ? 1 : 0),
);

export function clearExploreOnlyFilters() {
  batch(() => {
    exploreEra.value = null;
    exploreAlive.value = false;
    explorePremiere.value = false;
  });
}

/** The [from, to] window Explore aggregates over: the scope pill intersected with the
 *  shared time window (only when the reader set one deliberately). Nulls mean unbounded. */
export const exploreWindow = computed<{ from: string | null; to: string | null }>(() => {
  const deliberate = timeWindowTouched.value || timeWindowFromURL.value;
  const wFrom = deliberate ? timeFrom.value : null;
  const wTo = deliberate && timeTo.value ? timeTo.value : null;
  const today = todayISO();
  switch (exploreScope.value) {
    case "upcoming":
      return { from: wFrom && wFrom > today ? wFrom : today, to: wTo };
    case "past": {
      const yesterday = dayBeforeISO(today);
      return { from: wFrom, to: wTo && wTo < yesterday ? wTo : yesterday };
    }
    default:
      return { from: wFrom, to: wTo };
  }
});
export const theme = signal<"dark" | "light">(getInitialTheme());
/** Collapsible filter panel — session UI state, deliberately not URL-synced. */
export const filtersExpanded = signal(false);

// Map viewport bounds [west, south, east, north]
export const mapBounds = signal<[number, number, number, number] | null>(null);

// ── Unified list ───────────────────────────────────────────────────

/** Which side of the anchor the list is reading. Forward = today onward (soonest first);
 *  backward = before today (most recent first). Meaningless when anchorISO is null. */
export const listDirection = signal<"forward" | "backward">("forward");
/** True once the reader has picked a direction themselves (count-bar tab, popup count
 *  link, ?dir= in a shared URL). While false, the list may auto-switch to the side that
 *  actually has results — a composer with only past concerts must not land on an empty
 *  "Upcoming 0" that hides its 2,000 past events behind an unnoticed tab. Reset whenever
 *  the filters change (see queries.ts), so the auto-switch can help again. */
export const listDirectionExplicit = signal(false);

export const listItems = signal<ListItem[]>([]);
export const listLoading = signal(false);
/** True while a NEW query is replacing a list that already has cards (as opposed to the
 *  initial fetch or an infinite-scroll append): the list shows its updating state. */
export const listRefetching = signal(false);
export const listHasMore = signal(false);
export const listError = signal<string | null>(null);
/** Bumped when a *new* query replaces the results (not on page-append), so the list view
 *  scrolls back to its top only when filters actually change. */
export const listEpoch = signal(0);

/** Totals for both directions of the current window+filters (backward is a cheap probe
 *  when it isn't the direction being read). With no anchor, everything is "forward". */
export const forwardTotal = signal(0);
export const backwardTotal = signal(0);
/** Cancelled/postponed listings in the direction being read: shown in the list, left out of the
 *  totals above (and out of the map's marker counts) — ISSUES #069. */
export const notPerformedTotal = signal(0);
/** False until the first real totals arrive — lets the count bar show meta.json's static
 *  totals as a provisional figure instead of "0" during a cold start. */
export const totalsLoaded = signal(false);

// ── Map marker counts ──────────────────────────────────────────────

/** Per-city concert counts for the active filters (live pipeline, from /api/city-counts). */
export const cityCounts = signal<Map<number, CityCount>>(new Map());
export const cityCountsLoaded = signal(false);
/** Archive analogue, from /api/historical-city-counts. */
export const historicalCityCounts = signal<Map<number, number>>(new Map());
export const historicalCityCountsLoaded = signal(false);

/** Cities referenced by the archive export (~460 rows) — loaded once, mirrors `cities`
 *  but is NOT the same list: a historical-only tour stop may have zero modern concerts. */
export const historicalCities = signal<City[]>([]);

/** Countries that appear anywhere in the two city lists, with their concert counts —
 *  the options behind the country picker. Derived rather than fetched: cities.json
 *  already carries both the ISO code and the display name, so the filter needs no new
 *  aggregate. The archive list is merged in as well, so a country reachable only through
 *  the archives is still offered (its live count is simply 0). */
export const countryOptions = computed<{ code: string; name: string; count: number }[]>(() => {
  const by = new Map<string, { code: string; name: string; count: number }>();
  const add = (c: City) => {
    if (!c.country_code) return;
    const existing = by.get(c.country_code);
    if (existing) {
      existing.count += c.count;
      // Some cities carry the bare code as their country_name; a sibling city of the same
      // country may still have the real one. Take the first name that isn't the code.
      if (existing.name === existing.code) {
        existing.name = countryDisplayName(c.country_code, c.country_name);
      }
      return;
    }
    by.set(c.country_code, {
      code: c.country_code,
      name: countryDisplayName(c.country_code, c.country_name),
      count: c.count,
    });
  };
  for (const c of cities.value) add(c);
  for (const c of historicalCities.value) add(c);
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
});

/** Center of the active radius filter: the manually-selected city's record, once the
 *  static city lists have loaded. null while inactive or unresolvable — queries then fall
 *  back to the exact city_id match, and re-run when the coordinates arrive. */
export const radiusCenter = computed<City | null>(() => {
  if (radiusKm.value === null) return null;
  if (selectedCitySource.value !== "manual") return null;
  const id = selectedCityId.value;
  if (id === null) return null;
  return (
    cities.value.find((c) => c.id === id) ??
    historicalCities.value.find((c) => c.id === id) ??
    null
  );
});

/** Marker-narrowing predicate shared by both map layers: inside the radius when one is
 *  active, otherwise the exact selected city. */
function cityWithinFilter(city: City, cityId: number, center: City | null, km: number | null): boolean {
  if (center && km !== null) {
    return haversineKm(center.lat, center.lon, city.lat, city.lon) <= km;
  }
  return city.id === cityId;
}

export type MapFocusRequest =
  | {
      kind: "city" | "concert";
      cityId: number;
      /** Carried so the map popup needn't look the record up in a client-side corpus. */
      item?: ListItem;
      lat: number;
      lon: number;
      seq: number;
    }
  /** Zoom all the way out — fired when a discrete filter is picked, so matches outside
   *  the current viewport (including the far side of the globe) become visible. */
  | { kind: "world"; seq: number }
  /** Frame a geographic REGION rather than fly to a point: the picked city plus its
   *  vicinity, or a whole country. Always a bbox, never a zoom level, because any camera
   *  move arms the viewport bbox list filter (see Map.tsx) — a frame that merely centred
   *  the target could clip rows the filter itself legitimately includes. fitBounds with
   *  padding guarantees the region is inside the viewport, so the two agree. */
  | {
      kind: "bounds";
      /** [west, south, east, north] */
      bbox: [number, number, number, number];
      maxZoom: number;
      /** fitBounds padding override. The default (48px) deliberately insets a city/country
       *  frame so the camera can't clip rows the filter selected — but a Back-button VIEWPORT
       *  RESTORE wants the bbox reproduced as it was, so it passes 0. */
      padding?: number;
      seq: number;
    };
export const mapFocusRequest = signal<MapFocusRequest | null>(null);

// ── Derived map sources ────────────────────────────────────────────

/** Do the active filters make the *live* city counts non-default? Used to decide whether
 *  the static baked counts in cities.json are an honest provisional stand-in while the
 *  real counts are still in flight (cold start: markers at 0ms instead of after D1). */
const liveCountsAreDefault = computed(
  () =>
    selectedComposerId.value === null &&
    selectedPieceKey.value === null &&
    selectedOrgId.value === null &&
    !selectedGenre.value &&
    !timeWindowTouched.value &&
    !timeWindowFromURL.value,
);

/** City markers for the live layer: each city with ≥1 concert under the active filters.
 *  Country narrowing and the manual city filter are applied here, client-side — they only
 *  hide markers. Popup-sourced city filters deliberately don't narrow the map. */
export const liveMapCities = computed(() => {
  const usingFallback = !cityCountsLoaded.value;
  if (usingFallback && !liveCountsAreDefault.value) return [] as City[];
  const counts = cityCounts.value;
  const cityId = selectedCityId.value;
  const country = selectedCountry.value;
  const narrowToCity = cityId !== null && selectedCitySource.value !== "popup";
  const center = radiusCenter.value;
  const km = radiusKm.value;

  return cities.value
    .filter((city) => {
      if (!usingFallback && !counts.has(city.id)) return false;
      if (country && city.country_code !== country) return false;
      if (narrowToCity && !cityWithinFilter(city, cityId, center, km)) return false;
      return true;
    })
    .map((city) => {
      const c = usingFallback ? null : counts.get(city.id)!;
      // upCount null = split unknown (the baked fallback counts are all-time): the popup
      // must not claim "upcoming" for those.
      return { ...city, count: c ? c.n : city.count, upCount: c ? c.up : null };
    });
});

/** Archive layer markers. Same static-count fallback rationale as liveMapCities; also keeps
 *  a robustness net for a city referenced by list results but missing from the static
 *  aggregate (D1 ahead of the last static rebuild) — built from coordinates already in the
 *  concert record so a real event can't silently have no dot. */
export const historicalMapCities = computed(() => {
  const usingFallback = !historicalCityCountsLoaded.value;
  if (usingFallback && !liveCountsAreDefault.value) return [] as City[];
  const cityId = selectedCityId.value;
  const country = selectedCountry.value;
  const narrowToCity = cityId !== null && selectedCitySource.value !== "popup";
  const center = radiusCenter.value;
  const km = radiusKm.value;

  const byId = new Map<number, City>();
  for (const city of historicalCities.value) byId.set(city.id, city);
  for (const item of listItems.value) {
    if (item.kind !== "hist") continue;
    const c = item.concert;
    if (!byId.has(c.city_id)) {
      byId.set(c.city_id, {
        id: c.city_id,
        name: c.city_name,
        country_code: c.country_code,
        country_name: c.country_code,
        lat: c.lat,
        lon: c.lon,
        count: 0,
      });
    }
  }

  const out: City[] = [];
  if (usingFallback) {
    for (const city of historicalCities.value) {
      if (country && city.country_code !== country) continue;
      if (narrowToCity && !cityWithinFilter(city, cityId, center, km)) continue;
      out.push({ ...city });
    }
    return out;
  }
  for (const [id, n] of historicalCityCounts.value) {
    const city = byId.get(id);
    if (!city) continue;
    if (narrowToCity && !cityWithinFilter(city, cityId, center, km)) continue;
    if (country && city.country_code !== country) continue;
    out.push({ ...city, count: n });
  }
  return out;
});

/** The unified marker source: live and archive counts summed per city, each layer
 *  contributing only when its source(s) are enabled. One dot per city with the combined
 *  count, so the single-source clustering keeps working unchanged. */
export const mapCities = computed<City[]>(() => {
  const merged = new Map<number, City>();

  if (liveEnabled.value) {
    for (const city of liveMapCities.value) {
      merged.set(city.id, { ...city, liveCount: city.count, histCount: 0 });
    }
  }
  if (archiveEnabled.value) {
    for (const city of historicalMapCities.value) {
      const existing = merged.get(city.id);
      if (existing) {
        existing.histCount = (existing.histCount ?? 0) + city.count;
        existing.count += city.count;
      } else {
        // Archive events are all past-dated, so a hist-only city's upcoming side is 0.
        merged.set(city.id, { ...city, liveCount: 0, histCount: city.count, upCount: 0 });
      }
    }
  }
  return [...merged.values()];
});

/** Active filter count for the chips row. */
export const activeFilterCount = computed(() => {
  let count = 0;
  if (selectedComposerId.value !== null) count++;
  if (selectedPieceKey.value !== null) count++;
  if (selectedOrgId.value !== null) count++;
  if (selectedCityId.value !== null) count++;
  if (selectedCountry.value) count++;
  if (selectedGenre.value) count++;
  return count;
});

// ── Actions ────────────────────────────────────────────────────────

// Multi-signal updates MUST be batched: effects run synchronously after each
// unbatched write, so subscribers would observe half-applied states.

export function clearAllFilters() {
  batch(() => {
    clearExploreOnlyFilters();
    selectedComposerId.value = null;
    selectedPieceKey.value = null;
    selectedPiece.value = null;
    selectedOrgId.value = null;
    selectedCityId.value = null;
    selectedCitySource.value = null;
    radiusKm.value = null;
    selectedCountry.value = "";
    selectedGenre.value = "";
    disabledSources.value = new Set();
    resetTimeWindow();
    listDirection.value = "forward";
    mentionsReaderOpen.value = false;
    mentionCount.value = null;
    resetMapToWorld();
  });
}

export function clearCityFilter() {
  batch(() => {
    selectedCityId.value = null;
    selectedCitySource.value = null;
    radiusKm.value = null;
  });
}

export function setCityFilter(cityId: number | null, source: "manual" | "popup" = "manual") {
  batch(() => {
    selectedCityId.value = cityId;
    selectedCitySource.value = cityId === null ? null : source;
    // A popup selection is transient narrowing — it must not inherit (or later resurrect)
    // a radius that belonged to the manual pick it replaced.
    if (source === "popup" || cityId === null) radiusKm.value = null;
    // Mirror of setCountryFilter: whichever of the pair the reader just touched wins, so
    // the two can never contradict into an unexplained empty list. Manual picks only —
    // a popup city is by definition a marker that survived the country filter.
    if (source === "manual" && cityId !== null && selectedCountry.value) {
      const city =
        cities.value.find((c) => c.id === cityId) ??
        historicalCities.value.find((c) => c.id === cityId);
      if (city && city.country_code !== selectedCountry.value) selectedCountry.value = "";
    }
  });
}

export function clearPopupCityFilter(cityId: number) {
  if (selectedCitySource.value === "popup" && selectedCityId.value === cityId) {
    clearCityFilter();
  }
}

/** Pick (or clear) the country filter. A city outside the new country would leave the two
 *  filters contradicting each other and the list empty with no obvious culprit, so the
 *  narrower filter yields to the broader intent. */
export function setCountryFilter(code: string) {
  batch(() => {
    selectedCountry.value = code;
    if (!code) return;
    const id = selectedCityId.value;
    if (id === null) return;
    const city =
      cities.value.find((c) => c.id === id) ??
      historicalCities.value.find((c) => c.id === id);
    if (city && city.country_code !== code) clearCityFilter();
  });
}

export function clearCountryFilter() {
  selectedCountry.value = "";
}

/** Degrees of latitude/longitude per kilometre at a given latitude — good enough for
 *  framing a camera (it ignores the ellipsoid), which is all these bboxes are used for. */
function kmToDegrees(km: number, lat: number): { dLat: number; dLon: number } {
  const dLat = km / 111.32;
  // Guard the poles: cos(90°) → 0 would blow the longitude span up to infinity.
  const cos = Math.max(0.05, Math.cos((lat * Math.PI) / 180));
  return { dLat, dLon: km / (111.32 * cos) };
}

/** How much ground to show around a city when no radius is set. Wide enough to read as
 *  "the region", tight enough that the city is unmistakably the subject. */
const DEFAULT_CITY_VICINITY_KM = 30;

/** Frame a city and the area the filter actually covers. Fired when a city is picked in
 *  the filter panel (and when its radius changes): a reader who narrows to Kraków while
 *  looking at North America would otherwise see an unchanged map and an apparently empty
 *  region. `km` is the active radius, or a default "vicinity" box when the filter is the
 *  single city. */
export function focusCityAreaOnMap(city: { lat: number; lon: number }, km: number | null) {
  const half = km ?? DEFAULT_CITY_VICINITY_KM;
  const { dLat, dLon } = kmToDegrees(half, city.lat);
  mapFocusRequest.value = {
    kind: "bounds",
    bbox: [city.lon - dLon, city.lat - dLat, city.lon + dLon, city.lat + dLat],
    // Without a cap, the default box would drop a one-city filter onto the rooftops.
    maxZoom: 10,
    seq: Date.now(),
  };
}

/** Frame a country from the cities we hold for it. Falls back to the whole-globe view
 *  when the country has no usable coordinates, or when its cities straddle the
 *  antimeridian — a naive min/max bbox across the date line spans the planet, which would
 *  frame everything except the country asked for. */
export function focusCountryOnMap(code: string) {
  if (!code) {
    resetMapToWorld();
    return;
  }
  let west = Infinity, south = Infinity, east = -Infinity, north = -Infinity;
  const consider = (c: City) => {
    if (c.country_code !== code) return;
    if (!Number.isFinite(c.lat) || !Number.isFinite(c.lon)) return;
    west = Math.min(west, c.lon);
    east = Math.max(east, c.lon);
    south = Math.min(south, c.lat);
    north = Math.max(north, c.lat);
  };
  for (const c of cities.value) consider(c);
  for (const c of historicalCities.value) consider(c);

  if (!Number.isFinite(west) || east - west > 180) {
    resetMapToWorld();
    return;
  }
  // A country we know through a single city has a zero-area bbox; give it the same
  // vicinity box a city pick gets so fitBounds has something to work with.
  if (east - west < 0.01 && north - south < 0.01) {
    focusCityAreaOnMap({ lat: north, lon: east }, null);
    return;
  }
  mapFocusRequest.value = {
    kind: "bounds",
    bbox: [west, south, east, north],
    // City-states (Monaco, Singapore) would otherwise land at street level.
    maxZoom: 9,
    seq: Date.now(),
  };
}

/** Bump to ask the browse page to bring the reading list into view. Set by actions that
 *  change the list from somewhere far above it — the city popup's count links, which sit
 *  on the map. The list's own post-filter scroll deliberately only pulls the viewport UP
 *  (see UnifiedList), so without this the reader stays on the map and never sees the
 *  concerts they just asked for. */
export const listScrollRequest = signal(0);
export function requestListScroll() {
  listScrollRequest.value = Date.now();
}

export function clearComposerFilter() {
  batch(() => {
    selectedComposerId.value = null;
    mentionsReaderOpen.value = false;
    mentionCount.value = null;
  });
}

/** Both piece signals move together — see the note at their declaration. */
export function setPieceFilter(piece: Piece | null) {
  batch(() => {
    selectedPieceKey.value = piece ? piece.key : null;
    selectedPiece.value = piece;
  });
}

export function clearPieceFilter() {
  setPieceFilter(null);
}

export function clearOrgFilter() {
  selectedOrgId.value = null;
}

export function focusCityOnMap(city: City) {
  mapFocusRequest.value = {
    kind: "city",
    cityId: city.id,
    lat: city.lat,
    lon: city.lon,
    seq: Date.now(),
  };
}

export function focusItemOnMap(item: ListItem) {
  const c = item.concert;
  if (!hasMapLocation(c)) return; // a streamed concert's "Internet" city is not a place (#075)
  mapFocusRequest.value = {
    kind: "concert",
    cityId: c.city_id,
    item,
    lat: c.lat,
    lon: c.lon,
    seq: Date.now(),
  };
}

export function focusConcertOnMap(concert: Concert) {
  focusItemOnMap({ kind: "live", concert });
}

export function focusHistoricalConcertOnMap(concert: HistoricalConcert) {
  focusItemOnMap({ kind: "hist", concert });
}

export function clearMapFocus() {
  mapFocusRequest.value = null;
}

/** Zoom the map out to the whole globe. Fired when a discrete filter is chosen: a reader
 *  who picks a composer while zoomed into one region (or one hemisphere of the globe)
 *  would otherwise see a mostly-empty list and have to zoom out by hand. */
export function resetMapToWorld() {
  mapFocusRequest.value = { kind: "world", seq: Date.now() };
}

/** Put the camera back on a viewport the reader had earlier — the Back button (#013).
 *
 *  Selecting a composer or piece deliberately zooms out (`resetMapToWorld`, above) so matches
 *  outside the current viewport become visible. That is right on the way IN, but it used to be
 *  irreversible: `url-sync` pushed history entries with a null state object, so nothing recorded
 *  where the reader had been and Back dropped them at the default camera. The bbox now rides in
 *  the history entry's state and comes back through here.
 *
 *  padding 0 + a high maxZoom because this reproduces a real previous camera rather than framing
 *  a region: the 48px inset and zoom cap that suit a city frame would widen the view a little on
 *  every restore. */
export function restoreMapViewport(bbox: [number, number, number, number]) {
  mapFocusRequest.value = { kind: "bounds", bbox, maxZoom: 22, padding: 0, seq: Date.now() };
}

/** Jump from a Reader mention to its place on the map (only meaningful when the mention
 *  geocoded). Closes the drawer and flies to the city. */
export function focusMentionOnMap(m: {
  city_id?: number;
  lat?: number;
  lon?: number;
}) {
  if (m.city_id == null || m.lat == null || m.lon == null || !hasMapLocation(m)) return;
  batch(() => {
    mentionsReaderOpen.value = false;
    mapFocusRequest.value = {
      kind: "city",
      cityId: m.city_id!,
      lat: m.lat!,
      lon: m.lon!,
      seq: Date.now(),
    };
  });
}

// ── History write hint ──────────────────────────────────────────────
//
// url-sync pushes a history entry for discrete state changes (so Back works) and
// replaces for continuous/programmatic ones. Signal writes carry no provenance, so a
// programmatic writer that must NOT mint an entry (the auto direction switch, legacy
// URL-param canonicalization) sets this one-shot hint before writing.

let historyReplaceHint = false;
export function hintHistoryReplace(): void {
  historyReplaceHint = true;
}
export function consumeHistoryReplaceHint(): boolean {
  const v = historyReplaceHint;
  historyReplaceHint = false;
  return v;
}

// ── Helpers ─────────────────────────────────────────────────────────

/** ISO date of the day before `iso` (UTC arithmetic on a date-only string). */
export function dayBeforeISO(iso: string): string {
  const d = new Date(iso.slice(0, 10) + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function yearOfISO(iso: string): number | null {
  const y = Number(iso.slice(0, 4));
  return Number.isFinite(y) && y > 0 ? y : null;
}

function getInitialTheme(): "dark" | "light" {
  if (typeof window === "undefined") return "dark";
  const stored = localStorage.getItem("theme");
  if (stored === "light" || stored === "dark") return stored;
  if (window.matchMedia("(prefers-color-scheme: light)").matches) return "light";
  return "dark";
}

export function toggleTheme() {
  const next = theme.value === "dark" ? "light" : "dark";
  theme.value = next;
  localStorage.setItem("theme", next);
}
